import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  NOTIFICATIONS_PAGE_SIZE
} from '@/api/notificationsApi';
import { useActiveProfile, isActiveProfileValid } from '@/store/localStorage/useTokenState';
import { selectActiveProfile } from '@/store/user/selectors/selectActiveProfile';
import type { AppDispatch, RootState } from '@/app/store';
import { discussionsActions } from './discussionsSlice';
import type { DiscussionGroup, FilterMode } from './types';

const DISPLAY_SIZE = 20;

export function useDiscussions(filterMode: FilterMode) {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const displayPage = Number(searchParams.get('page') || '1');

  const { activeProfileId } = useActiveProfile();
  const myBlogSlug: string =
    useSelector((state: RootState) => selectActiveProfile(state, { activeProfileId }))?.blogSlug ?? '';

  const { ownBlogGroups, otherBlogGroups, mentionGroups, nextApiPage, hasMoreFromApi } = useSelector(
    (state: RootState) => state.discussions
  );

  const allGroups = useMemo<DiscussionGroup[]>(() => {
    let groups: DiscussionGroup[];
    if (filterMode === 'ownBlog') groups = Object.values(ownBlogGroups);
    else if (filterMode === 'otherBlogs') groups = Object.values(otherBlogGroups);
    else groups = Object.values(mentionGroups);
    return groups.sort((a, b) => b.latestAt.localeCompare(a.latestAt));
  }, [ownBlogGroups, otherBlogGroups, mentionGroups, filterMode]);

  const neededGroups = displayPage * DISPLAY_SIZE;
  const shouldFetchMore = allGroups.length < neededGroups && hasMoreFromApi;

  const { data, isFetching, isError } = useGetNotificationsQuery(
    { profileId: activeProfileId ?? '', page: nextApiPage, pageSize: NOTIFICATIONS_PAGE_SIZE },
    { skip: !shouldFetchMore || !isActiveProfileValid(activeProfileId) }
  );

  useEffect(() => {
    if (!data) return;
    dispatch(discussionsActions.appendNotifications({ notifications: data, myBlogSlug, activeProfileId: activeProfileId ?? '' }));
  }, [data, dispatch, myBlogSlug, activeProfileId]);

  useEffect(() => {
    dispatch(discussionsActions.resetAll());
  }, [activeProfileId, dispatch]);

  const [markAllRead, { isLoading: isMarkingAllRead, isSuccess: isMarkAllSuccess }] =
    useMarkAllNotificationsReadMutation();

  useEffect(() => {
    if (isMarkAllSuccess) {
      dispatch(discussionsActions.resetAll());
    }
  }, [isMarkAllSuccess, dispatch]);

  const handleMarkAllRead = () => {
    if (isActiveProfileValid(activeProfileId) && activeProfileId) {
      markAllRead({ profileId: activeProfileId });
    }
  };

  const displayStart = (displayPage - 1) * DISPLAY_SIZE;
  const discussions = allGroups.slice(displayStart, displayStart + DISPLAY_SIZE);

  const totalRecords = allGroups.length + (hasMoreFromApi ? DISPLAY_SIZE : 0);
  const showPagination = hasMoreFromApi || allGroups.length > DISPLAY_SIZE;

  return {
    discussions,
    currentPage: displayPage,
    totalRecords,
    pageSize: DISPLAY_SIZE,
    showPagination,
    isFetching: shouldFetchMore || isFetching,
    isError,
    isMarkingAllRead,
    handleMarkAllRead
  };
}
